<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import { useRadioLogStore } from '@/stores/radioLog'
import { useTeamsStore } from '@/stores/teams'
import { useAssignmentsStore } from '@/stores/assignments'
import { MESSAGE_TYPES, MessageType, PRIORITY_TRAFFIC_PLAN_LABELS } from '@/types'
import type { RadioLogEntry, Assignment } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

const props = defineProps<{
  incidentId: string
  opId: string
  canEdit: boolean
  canDelete: boolean
  permissionRole?: string | null
  searchQuery?: string
}>()

const emit = defineEmits<{
  delete: [entryId: string]
}>()

const radioLogStore = useRadioLogStore()
const teamsStore = useTeamsStore()
const assignmentsStore = useAssignmentsStore()

// ─── Filtered entries ─────────────────────────────────────────────────────────

const entries = computed(() => {
  const q = props.searchQuery?.trim().toLowerCase()
  if (!q) return radioLogStore.entries
  return radioLogStore.entries.filter(
    (e) =>
      e.team_identifier.toLowerCase().includes(q) ||
      e.message_type.toLowerCase().includes(q) ||
      (e.notes?.toLowerCase().includes(q) ?? false),
  )
})

// ─── Display helpers ──────────────────────────────────────────────────────────

function formatTime(entry: RadioLogEntry): string {
  if (!entry.client_timestamp) return '—'
  const date = entry.client_timestamp.toDate()
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

// ─── Inline edit state ────────────────────────────────────────────────────────

const editingEntryId = ref<string | null>(null)
const saving = ref(false)

const editForm = ref({
  team_identifier: '',
  message_type: '',
  assignment_id: null as string | null,
  assignment_name: null as string | null,
  priority_traffic_plan: null as string | null,
  notes: null as string | null,
})

// Autocomplete state for the edit row
const teamSuggestions = ref<string[]>([])
const messageTypeSuggestions = ref<string[]>([])
const assignmentSuggestions = ref<Assignment[]>([])

const ASSIGNMENT_MESSAGE_TYPES: string[] = [
  MessageType.StartingAssignment,
  MessageType.CompletedAssignment,
]

const editNeedsAssignment = computed(() =>
  ASSIGNMENT_MESSAGE_TYPES.includes(editForm.value.message_type),
)

const editNeedsPriorityPlan = computed(() => editForm.value.message_type === MessageType.PriorityTraffic)

const editMatchedTeam = computed(() =>
  teamsStore.teams.find((t) => t.team_identifier === editForm.value.team_identifier.trim()) ?? null
)

const editTeamAssignmentIds = computed(() => {
  if (!editMatchedTeam.value) return new Set<string>()
  return new Set(
    assignmentsStore.getTeamAssignments(editMatchedTeam.value.team_id).map((ta) => ta.assignment_id),
  )
})

function startEdit(entry: RadioLogEntry) {
  editingEntryId.value = entry.entry_id
  editForm.value = {
    team_identifier: entry.team_identifier,
    message_type: entry.message_type,
    assignment_id: entry.assignment_id,
    assignment_name: entry.assignment_name,
    priority_traffic_plan: entry.priority_traffic_plan ?? null,
    notes: entry.notes,
  }
  teamSuggestions.value = []
  messageTypeSuggestions.value = []
  assignmentSuggestions.value = []
}

function cancelEdit() {
  editingEntryId.value = null
  saving.value = false
}

async function saveEdit(entry: RadioLogEntry) {
  if (saving.value) return
  const team = editForm.value.team_identifier.trim()
  if (!team || !MESSAGE_TYPES.includes(editForm.value.message_type)) return

  saving.value = true
  try {
    const matchedTeam = teamsStore.teams.find((t) => t.team_identifier === team)
    await radioLogStore.editEntry(
      props.incidentId,
      props.opId,
      entry.entry_id,
      {
        team_identifier: team,
        team_id: matchedTeam?.team_id ?? entry.team_id,
        message_type: editForm.value.message_type,
        assignment_id: editForm.value.assignment_id,
        assignment_name: editForm.value.assignment_name,
        priority_traffic_plan: editForm.value.priority_traffic_plan || null,
        notes: editForm.value.notes?.trim() || null,
      },
      {
        team_identifier: entry.team_identifier,
        message_type: entry.message_type,
        notes: entry.notes,
      },
    )
    editingEntryId.value = null
  } finally {
    saving.value = false
  }
}

// ─── Edit row autocomplete helpers ───────────────────────────────────────────

function searchEditTeams(q: string) {
  const lower = q.toLowerCase()
  teamSuggestions.value = teamsStore.teams
    .map((t) => t.team_identifier)
    .filter((id) => id.toLowerCase().includes(lower))
}

function searchEditMessageTypes(q: string) {
  messageTypeSuggestions.value = q
    ? MESSAGE_TYPES.filter((t) => t.toLowerCase().startsWith(q.toLowerCase()))
    : [...MESSAGE_TYPES]
}

function searchEditAssignments(q: string) {
  const all = q
    ? assignmentsStore.assignments.filter((a) =>
        a.assignment_name.toLowerCase().includes(q.toLowerCase()),
      )
    : [...assignmentsStore.assignments]

  if (editForm.value.message_type === MessageType.StartingAssignment && editMatchedTeam.value) {
    const ids = editTeamAssignmentIds.value
    assignmentSuggestions.value = [
      ...all.filter((a) => ids.has(a.assignment_id)),
      ...all.filter((a) => !ids.has(a.assignment_id)),
    ]
  } else {
    assignmentSuggestions.value = all
  }
}

const editAssignmentGroupSplit = computed(() => {
  if (editForm.value.message_type !== MessageType.StartingAssignment || !editMatchedTeam.value) return 0
  const ids = editTeamAssignmentIds.value
  return assignmentSuggestions.value.filter((a) => ids.has(a.assignment_id)).length
})

function selectEditTeam(val: string) {
  editForm.value.team_identifier = val
  teamSuggestions.value = []
}

function selectEditMessageType(val: string) {
  editForm.value.message_type = val
  messageTypeSuggestions.value = []
  if (!ASSIGNMENT_MESSAGE_TYPES.includes(val)) {
    editForm.value.assignment_id = null
    editForm.value.assignment_name = null
  }
  if (val !== MessageType.PriorityTraffic) {
    editForm.value.priority_traffic_plan = null
  }
}

function selectEditAssignment(a: Assignment) {
  editForm.value.assignment_id = a.assignment_id
  editForm.value.assignment_name = a.assignment_name
  assignmentSuggestions.value = []
}

function onEditNotesKeydown(e: KeyboardEvent, entry: RadioLogEntry) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    saveEdit(entry)
  } else if (e.key === 'Escape') {
    cancelEdit()
  }
}

const inputClass = 'w-full bg-surface-container-high text-on-surface font-mono text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/30 placeholder:text-outline/50'
</script>

<template>
  <div class="bg-surface-container overflow-hidden">
    <div v-if="radioLogStore.isLoading" class="flex justify-center items-center py-12">
      <span class="font-mono text-xs text-outline uppercase tracking-widest">Loading...</span>
    </div>

    <template v-else>
      <div v-if="entries.length === 0" class="py-12 text-center font-mono text-xs text-outline uppercase tracking-widest">
        {{ props.searchQuery?.trim() ? 'No entries match your search' : 'No radio log entries for this operational period' }}
      </div>

      <table v-else class="w-full text-left border-collapse">
        <thead>
          <tr class="bg-surface-container-lowest/80 border-b border-border">
            <th class="px-4 py-3 font-headline text-[0.7rem] font-black uppercase tracking-widest text-outline w-36">Time</th>
            <th class="px-4 py-3 font-headline text-[0.7rem] font-black uppercase tracking-widest text-outline w-24">Team</th>
            <th class="px-4 py-3 font-headline text-[0.7rem] font-black uppercase tracking-widest text-outline">Message</th>
            <th class="px-4 py-3 font-headline text-[0.7rem] font-black uppercase tracking-widest text-outline">Notes</th>
            <th class="px-4 py-3 font-headline text-[0.7rem] font-black uppercase tracking-widest text-outline">Operator</th>
            <th class="px-4 py-3 w-20" />
          </tr>
        </thead>
        <tbody class="font-body text-xs">
          <template v-for="(entry, i) in entries" :key="entry.entry_id">

            <!-- ── Edit row ── -->
            <tr
              v-if="editingEntryId === entry.entry_id"
              class="border-b border-border bg-surface-container-low"
            >
              <td colspan="6" class="px-4 py-3">
                <div
                  class="grid gap-2 items-end"
                  :class="editNeedsAssignment || editNeedsPriorityPlan
                    ? 'grid-cols-[1fr_1fr_1fr_2fr_auto_auto]'
                    : 'grid-cols-[1fr_1fr_2fr_auto_auto]'"
                >
                  <!-- Team -->
                  <div class="flex flex-col gap-1">
                    <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Team</label>
                    <div class="relative">
                      <input
                        v-model="editForm.team_identifier"
                        :class="inputClass"
                        autocomplete="off"
                        @input="searchEditTeams(editForm.team_identifier)"
                        @focus="searchEditTeams(editForm.team_identifier)"
                        @blur="nextTick(() => { teamSuggestions = [] })"
                      />
                      <div
                        v-if="teamSuggestions.length > 0"
                        class="absolute z-50 top-full left-0 right-0 bg-surface-container-high border-t border-border shadow-xl max-h-40 overflow-y-auto"
                      >
                        <button
                          v-for="s in teamSuggestions"
                          :key="s"
                          type="button"
                          class="w-full text-left px-3 py-2 font-mono text-sm text-on-surface hover:bg-surface-container-highest transition-colors"
                          @mousedown.prevent="selectEditTeam(s)"
                        >
                          {{ s }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Message Type -->
                  <div class="flex flex-col gap-1">
                    <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Message</label>
                    <div class="relative">
                      <input
                        v-model="editForm.message_type"
                        :class="inputClass"
                        autocomplete="off"
                        @input="searchEditMessageTypes(editForm.message_type)"
                        @focus="searchEditMessageTypes(editForm.message_type)"
                        @blur="nextTick(() => { messageTypeSuggestions = [] })"
                      />
                      <div
                        v-if="messageTypeSuggestions.length > 0"
                        class="absolute z-50 top-full left-0 right-0 bg-surface-container-high border-t border-border shadow-xl max-h-48 overflow-y-auto"
                      >
                        <button
                          v-for="s in messageTypeSuggestions"
                          :key="s"
                          type="button"
                          class="w-full text-left px-3 py-2 font-mono text-sm text-on-surface hover:bg-surface-container-highest transition-colors"
                          @mousedown.prevent="selectEditMessageType(s)"
                        >
                          {{ s }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Assignment (conditional) -->
                  <div v-if="editNeedsAssignment" class="flex flex-col gap-1">
                    <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Assignment</label>
                    <div class="relative">
                      <input
                        :value="editForm.assignment_name ?? ''"
                        :class="inputClass"
                        placeholder="Search or type..."
                        autocomplete="off"
                        @input="(e) => { editForm.assignment_name = (e.target as HTMLInputElement).value; searchEditAssignments(editForm.assignment_name ?? '') }"
                        @focus="searchEditAssignments(editForm.assignment_name ?? '')"
                        @blur="nextTick(() => { assignmentSuggestions = [] })"
                      />
                      <div
                        v-if="assignmentSuggestions.length > 0"
                        class="absolute z-50 top-full left-0 right-0 bg-surface-container-high border-t border-border shadow-xl max-h-48 overflow-y-auto"
                      >
                        <template v-for="(a, i) in assignmentSuggestions" :key="a.assignment_id">
                          <div
                            v-if="i === 0 && editAssignmentGroupSplit > 0"
                            class="px-3 py-1 font-mono text-[0.6rem] uppercase tracking-widest text-outline bg-surface-container-lowest/60 border-b border-border"
                          >
                            Assigned
                          </div>
                          <div
                            v-if="editAssignmentGroupSplit > 0 && i === editAssignmentGroupSplit"
                            class="px-3 py-1 font-mono text-[0.6rem] uppercase tracking-widest text-outline bg-surface-container-lowest/60 border-y border-border"
                          >
                            Other
                          </div>
                          <button
                            type="button"
                            class="w-full text-left px-3 py-2 font-mono text-sm text-on-surface hover:bg-surface-container-highest transition-colors"
                            @mousedown.prevent="selectEditAssignment(a)"
                          >
                            {{ a.assignment_name }}
                          </button>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Priority Traffic Plan (conditional) -->
                  <div v-if="editNeedsPriorityPlan" class="flex flex-col gap-1">
                    <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Plan</label>
                    <div class="flex gap-1">
                      <button
                        v-for="(label, plan) in PRIORITY_TRAFFIC_PLAN_LABELS"
                        :key="plan"
                        type="button"
                        :class="[
                          'flex-1 font-mono text-sm px-3 py-2 border transition-colors focus:outline-none',
                          editForm.priority_traffic_plan === plan
                            ? 'bg-primary-container text-white border-primary-container'
                            : 'bg-surface-container-high text-on-surface border-border hover:bg-surface-container-highest',
                        ]"
                        :title="label"
                        @click="editForm.priority_traffic_plan = plan"
                      >
                        {{ plan }}
                      </button>
                    </div>
                  </div>

                  <!-- Notes -->
                  <div class="flex flex-col gap-1">
                    <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Notes</label>
                    <input
                      :value="editForm.notes ?? ''"
                      :class="inputClass"
                      placeholder="Optional notes..."
                      autocomplete="off"
                      @input="(e) => { editForm.notes = (e.target as HTMLInputElement).value }"
                      @keydown="onEditNotesKeydown($event, entry)"
                    />
                  </div>

                  <!-- Save -->
                  <FosButton
                    variant="primary"
                    size="sm"
                    :disabled="saving || !editForm.team_identifier.trim() || !MESSAGE_TYPES.includes(editForm.message_type)"
                    @click="saveEdit(entry)"
                  >
                    {{ saving ? '...' : 'Save' }}
                  </FosButton>

                  <!-- Cancel -->
                  <FosButton
                    variant="secondary"
                    size="sm"
                    :disabled="saving"
                    @click="cancelEdit"
                  >
                    Cancel
                  </FosButton>
                </div>
              </td>
            </tr>

            <!-- ── Display row ── -->
            <tr
              v-else
              :class="[
                'border-b border-border hover:bg-surface-container-low transition-colors',
                i % 2 === 0 ? 'bg-surface-container-lowest/30' : '',
              ]"
            >
              <td class="px-4 py-3 text-on-surface-variant whitespace-nowrap font-mono">
                {{ formatTime(entry) }}
                <span
                  v-if="entry.edited_at"
                  class="ml-1.5 font-mono text-[0.6rem] text-outline/50 uppercase tracking-wide"
                  :title="entry.edited_by_name ? `Edited by ${entry.edited_by_name}` : 'Edited'"
                >· edited</span>
              </td>
              <td class="px-4 py-3 text-on-surface font-bold font-mono">{{ entry.team_identifier }}</td>
              <td class="px-4 py-3">
                <div class="flex flex-col gap-0.5">
                  <span
                    :class="{
                      'text-error font-bold': entry.message_type === 'Emergency Traffic',
                      'text-status-rtb font-bold': entry.message_type === 'Priority Traffic',
                      'text-on-surface-variant': entry.message_type !== 'Emergency Traffic' && entry.message_type !== 'Priority Traffic',
                    }"
                  >
                    {{ entry.message_type }}
                  </span>
                  <span v-if="entry.assignment_name" class="text-outline text-[0.65rem] font-mono">
                    {{ entry.assignment_name }}
                  </span>
                  <span v-if="entry.priority_traffic_plan" class="text-outline text-[0.65rem] font-mono">
                    {{ PRIORITY_TRAFFIC_PLAN_LABELS[entry.priority_traffic_plan] ?? `Plan ${entry.priority_traffic_plan}` }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-on-surface-variant">{{ entry.notes || '' }}</td>
              <td class="px-4 py-3 text-outline text-[0.65rem]">{{ entry.operator_name }}</td>
              <td class="px-4 py-3 text-right">
                <div class="flex items-center justify-end gap-1">
                  <PermissionGuard v-if="editingEntryId === null" action="radio_log.edit_entry" :role="permissionRole">
                    <FosButton
                      variant="tertiary"
                      size="sm"
                      aria-label="Edit entry"
                      @click="startEdit(entry)"
                    >
                      <span class="material-symbols-outlined text-[13px] text-outline hover:text-on-surface transition-colors cursor-pointer">edit</span>
                    </FosButton>
                  </PermissionGuard>
                  <PermissionGuard action="radio_log.delete_entry" :role="permissionRole">
                    <FosButton
                      variant="tertiary"
                      size="sm"
                      aria-label="Delete entry"
                      @click="emit('delete', entry.entry_id)"
                    >
                      <span class="material-symbols-outlined text-[16px] text-outline hover:text-error transition-colors cursor-pointer">close</span>
                    </FosButton>
                  </PermissionGuard>
                </div>
              </td>
            </tr>

          </template>
        </tbody>
      </table>
    </template>
  </div>
</template>
